import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { PortFormData } from "@/types/port";

interface AddPortDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  countries: any[];
  cities: any[];
}

export const AddPortDialog = ({ open, onOpenChange, onSuccess, countries, cities }: AddPortDialogProps) => {
  const [formData, setFormData] = useState<PortFormData>({
    name_ar: "",
    name_en: "",
    port_code: "",
    country_id: "",
    city_id: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const filteredCities = cities.filter(city => city.country_id === formData.country_id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.from("ports").insert([formData]);

      if (error) throw error;

      toast.success("تم إضافة الميناء بنجاح");
      setFormData({
        name_ar: "",
        name_en: "",
        port_code: "",
        country_id: "",
        city_id: "",
      });
      onSuccess();
    } catch (error) {
      console.error("Error adding port:", error);
      toast.error("حدث خطأ أثناء إضافة الميناء");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCountryChange = (countryId: string) => {
    setFormData(prev => ({
      ...prev,
      country_id: countryId,
      city_id: "" // Reset city when country changes
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة ميناء جديد</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name_ar">الاسم بالعربية *</Label>
            <Input
              id="name_ar"
              value={formData.name_ar}
              onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
              placeholder="أدخل اسم الميناء بالعربية"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
            <Input
              id="name_en"
              value={formData.name_en}
              onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
              placeholder="أدخل اسم الميناء بالإنجليزية"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="port_code">كود الميناء *</Label>
            <Input
              id="port_code"
              value={formData.port_code}
              onChange={(e) => setFormData(prev => ({ ...prev, port_code: e.target.value.toUpperCase() }))}
              placeholder="أدخل كود الميناء (مثل: JED)"
              required
              maxLength={5}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="country_id">الدولة</Label>
            <Select value={formData.country_id} onValueChange={handleCountryChange}>
              <SelectTrigger>
                <SelectValue placeholder="اختر الدولة" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.id} value={country.id}>
                    {country.name_ar}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="city_id">المدينة</Label>
            <Select 
              value={formData.city_id} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, city_id: value }))}
              disabled={!formData.country_id}
            >
              <SelectTrigger>
                <SelectValue placeholder="اختر المدينة" />
              </SelectTrigger>
              <SelectContent>
                {filteredCities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>
                    {city.name_ar}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "جاري الإضافة..." : "إضافة الميناء"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};